import {client} from "@/httpClient";
import {locationAtom} from "@/store/jotaiAtoms";
import useTokenStore from "@/store/token";
import {handleErrorWithToast, type TError} from "@/utils/errorHandlers";
import "@tanstack/react-query";
import {MutationCache, QueryCache, QueryClient, type Mutation, type QueryFunction} from "@tanstack/react-query";
import type {AxiosError} from "axios";
import i18next from "i18next";
import {getDefaultStore} from "jotai";
import {toast} from "react-toastify";

declare module "@tanstack/react-query" {
  interface Register {
    defaultError: AxiosError;
    mutationMeta: {
      errorCount?: number;
      errorMessage?: string;
      skipErrorToast?: boolean;
      skipSuccessToast?: boolean;
      successMessage?: string;
    };
  }
}

const ROUTES_WITHOUT_UNAUTHORIZED_REDIRECT = ["assets", "management", "management_stats"] as const;

const defaultQueryFn: QueryFunction = async ({queryKey, pageParam}) => {
  if (!!pageParam) {
    return await client.get(
      (Number(pageParam) ? `${queryKey[0]}?page=${pageParam}` + ((queryKey[1] as string) ?? "") : pageParam) as string,
    );
  } else {
    return await client.get(queryKey[0] as string);
  }
};

const mutationCache = new MutationCache({
  onError(error) {
    const mutation: Mutation = arguments[3];

    if (mutation.options.meta?.skipErrorToast) {
      return;
    }

    handleErrorWithToast({
      error: error as TError,
      count: mutation.options.meta?.errorCount,
      defaultMessage: mutation.options.meta?.errorMessage,
    });
  },
  onSuccess() {
    const mutation: Mutation = arguments[3];

    if (mutation.options.meta?.skipSuccessToast) {
      return;
    }

    toast((mutation.options.meta?.successMessage as string) ?? i18next.t("toast_global_success"), {
      type: "success",
    });
  },
});

const queryCache = new QueryCache({
  async onError(error: AxiosError) {
    if (
      error.response?.status === 401 &&
      !ROUTES_WITHOUT_UNAUTHORIZED_REDIRECT.some(route => error.config?.url?.includes(route))
    ) {
      await useTokenStore.getState().logout();
      getDefaultStore().set(locationAtom, {
        previousRoute: getDefaultStore().get(locationAtom).currentRoute,
        currentRoute: `${location.pathname}${location.search}`,
      });
    }
  },
});

const queryClient = new QueryClient({
  queryCache,
  mutationCache,
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      queryFn: defaultQueryFn,
      refetchOnMount: false,
      retry: false,
    },
    mutations: {},
  },
});

export {queryCache, queryClient};
